/** Tipos partilhados do módulo SEO (painel + público). */

export type SeoEntityType = 'guide' | 'category' | 'power' | 'ace' | 'page';

export type SeoStatus = 'configured' | 'partial' | 'empty' | 'noindex';

export type SeoGlobalConfig = {
  siteName: string;
  defaultTitle: string;
  defaultDescription: string;
  defaultCanonicalBase: string;
  defaultShareImage: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  twitterCard: string;
  robotsIndex: boolean;
  robotsFollow: boolean;
  sitemapEnabled: boolean;
};

export type SeoEntryFields = {
  id: number | null;
  entityType: SeoEntityType;
  entityId: number | null;
  pageKey: string | null;
  title: string;
  description: string;
  canonicalUrl: string;
  slugOverride: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  twitterCard: string;
  shareImage: string;
  robotsIndex: boolean | null;
  robotsFollow: boolean | null;
  schemaJson: Record<string, unknown> | null;
  updatedAt: string | null;
};

export type SeoCatalogItem = {
  entityType: SeoEntityType;
  entityId: number | null;
  pageKey: string | null;
  label: string;
  path: string;
  status: SeoStatus;
  publicationStatus?: string | null;
  robotsIndex?: boolean | null;
  updatedAt?: string | null;
};

export type SeoResolved = {
  title: string;
  description: string;
  canonical: string;
  shareImage: string;
  og: {
    title: string;
    description: string;
    image: string;
    type: string;
    siteName: string;
  };
  twitter: {
    card: string;
    title: string;
    description: string;
    image: string;
  };
  robots: {
    index: boolean;
    follow: boolean;
    content: string;
  };
  schema: Record<string, unknown> | null;
  entity?: Record<string, unknown>;
  status?: SeoStatus;
};

export type SeoEntryPayload = {
  entityType: SeoEntityType;
  entityId?: number | null;
  pageKey?: string | null;
  title?: string;
  description?: string;
  canonicalUrl?: string;
  slugOverride?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterCard?: string;
  shareImage?: string;
  robotsIndex?: boolean | null;
  robotsFollow?: boolean | null;
  schemaJson?: Record<string, unknown> | null;
};

export type SeoEntryResponse = {
  entry: SeoEntryFields;
  entity: {
    type: string;
    id?: number | null;
    pageKey?: string | null;
    label: string;
    path: string;
    fallbackTitle?: string;
    fallbackDescription?: string;
    status?: string;
    categoryName?: string;
  };
  status: SeoStatus;
  resolved: SeoResolved;
};

export const SEO_TITLE_GOOD_MIN = 30;
export const SEO_TITLE_GOOD_MAX = 60;
export const SEO_DESC_GOOD_MIN = 70;
export const SEO_DESC_GOOD_MAX = 160;

export type SeoHintLevel = 'good' | 'warn' | 'bad';

export type SeoHint = {
  id: string;
  level: SeoHintLevel;
  message: string;
};

export function buildSeoHints(fields: Pick<SeoEntryFields, 'title' | 'description' | 'ogImage' | 'shareImage' | 'canonicalUrl' | 'slugOverride' | 'robotsIndex'>, fallbackTitle = '', fallbackDescription = ''): SeoHint[] {
  const hints: SeoHint[] = [];
  const title = (fields.title || fallbackTitle).trim();
  const desc = (fields.description || fallbackDescription).trim();
  const titleLen = title.length;
  const descLen = desc.length;

  if (!title) {
    hints.push({ id: 'title-missing', level: 'bad', message: 'Falta um título SEO (será usado o título da entidade ou o global).' });
  } else if (titleLen < SEO_TITLE_GOOD_MIN) {
    hints.push({ id: 'title-short', level: 'warn', message: `Title curto (${titleLen} caracteres). Ideal: ${SEO_TITLE_GOOD_MIN}–${SEO_TITLE_GOOD_MAX}.` });
  } else if (titleLen > SEO_TITLE_GOOD_MAX) {
    hints.push({ id: 'title-long', level: 'warn', message: `Title longo (${titleLen} caracteres). Pode ser cortado no Google.` });
  } else {
    hints.push({ id: 'title-ok', level: 'good', message: `Title com bom comprimento (${titleLen} caracteres).` });
  }

  if (!desc) {
    hints.push({ id: 'desc-missing', level: 'bad', message: 'Falta meta description.' });
  } else if (descLen < SEO_DESC_GOOD_MIN) {
    hints.push({ id: 'desc-short', level: 'warn', message: `Description curta (${descLen}). Ideal: ${SEO_DESC_GOOD_MIN}–${SEO_DESC_GOOD_MAX}.` });
  } else if (descLen > SEO_DESC_GOOD_MAX) {
    hints.push({ id: 'desc-long', level: 'warn', message: `Description longa (${descLen}). Pode ser truncada nos resultados.` });
  } else {
    hints.push({ id: 'desc-ok', level: 'good', message: `Description com bom comprimento (${descLen} caracteres).` });
  }

  if (!fields.ogImage?.trim() && !fields.shareImage?.trim()) {
    hints.push({ id: 'og-image', level: 'warn', message: 'Sem imagem Open Graph / partilha.' });
  } else {
    hints.push({ id: 'og-image-ok', level: 'good', message: 'Imagem de partilha definida.' });
  }

  if (fields.canonicalUrl && fields.canonicalUrl.length > 120) {
    hints.push({ id: 'url-long', level: 'warn', message: 'URL canónica muito longa.' });
  }

  if (fields.slugOverride && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(fields.slugOverride)) {
    hints.push({ id: 'slug-invalid', level: 'bad', message: 'Slug inválido — use apenas minúsculas, números e hífens.' });
  }

  if (fields.robotsIndex === false) {
    hints.push({ id: 'noindex', level: 'warn', message: 'Página marcada como noindex — não aparecerá no Google.' });
  }

  return hints;
}

export function emptySeoEntryFields(
  entityType: SeoEntityType,
  entityId: number | null = null,
  pageKey: string | null = null
): SeoEntryFields {
  return {
    id: null,
    entityType,
    entityId,
    pageKey,
    title: '',
    description: '',
    canonicalUrl: '',
    slugOverride: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    twitterCard: '',
    shareImage: '',
    robotsIndex: null,
    robotsFollow: null,
    schemaJson: null,
    updatedAt: null,
  };
}
